import type { SitemapUrl } from "../config/sitemapUrls";
import type { ContentBlock, ContentPage, ContentSection } from "./types";
import aiCharacterVoices from "./blog/ai-character-voices";
import aiSingingVoiceGenerator from "./blog/ai-singing-voice-generator";
import aiVoiceCloning from "./blog/ai-voice-cloning";
import aiVoiceModels from "./blog/ai-voice-models";
import howToMakeAnAiDuet from "./blog/how-to-make-an-ai-duet";
import howToTrainAiVoiceModel from "./blog/how-to-train-an-ai-voice-model";
import voicifyAiIsNowJammable from "./blog/voicify-ai-is-now-jammable";
import whatIsRvc from "./blog/what-is-rvc";
import whyDoesMyAiCoverSoundRobotic from "./blog/why-does-my-ai-cover-sound-robotic";
import aiVoiceChangerForSinging from "./compare/ai-voice-changer-for-singing";
import bestAiCoverGenerators from "./compare/best-ai-cover-generators";
import coversAiAlternative from "./compare/covers-ai-alternative";
import easyAiVoiceAlternative from "./compare/easy-ai-voice-alternative";
import jammableVsElevenlabs from "./compare/jammable-vs-elevenlabs";
import jammableVsKitsAi from "./compare/jammable-vs-kits-ai";
import musicfyAlternative from "./compare/musicfy-alternative";

// Every blog and comparison page. A page that isn't here doesn't exist: no
// route, no sitemap entry, no Markdown copy.
export const CONTENT: ContentPage[] = [
  howToTrainAiVoiceModel,
  bestAiCoverGenerators,
  voicifyAiIsNowJammable,
  coversAiAlternative,
  whatIsRvc,
  jammableVsKitsAi,
  jammableVsElevenlabs,
  aiSingingVoiceGenerator,
  aiVoiceCloning,
  easyAiVoiceAlternative,
  aiVoiceModels,
  aiCharacterVoices,
  aiVoiceChangerForSinging,
  musicfyAlternative,
  howToMakeAnAiDuet,
  whyDoesMyAiCoverSoundRobotic,
];

export function contentPath(page: Pick<ContentPage, "section" | "slug">) {
  return `/${page.section}/${page.slug}`;
}

export function getContent(section: ContentSection, slug: string) {
  return CONTENT.find((p) => p.section === section && p.slug === slug);
}

// The Markdown strings one block carries, in render order: an md or callout
// block's text, or each step body of a steps block.
export function blockMd(b: ContentBlock): string[] {
  if (b.type === "md" || b.type === "callout") return [b.text];
  if (b.type === "steps") return b.items.map((i) => i.body);
  return [];
}

// Every Markdown string on a page, in render order. getStaticProps
// serializes them in this order and ContentArticle hands each block its
// share back by the same count.
export function mdSources(page: ContentPage): string[] {
  return page.blocks.flatMap(blockMd);
}

// Words / 220 a minute, at least 1.
export function readingMinutes(page: ContentPage): number {
  const picks = page.blocks.flatMap((b) =>
    b.type === "picks"
      ? b.items.flatMap((p) => [p.body, ...(p.pros ?? []), ...(p.cons ?? [])])
      : b.type === "verdicts"
        ? b.items.map((v) => v.why)
        : [],
  );
  const text = [page.answer, ...mdSources(page), ...picks].join(" ");
  return Math.max(1, Math.round(text.split(/\s+/).length / 220));
}

// Newest first.
export function listContent(section: ContentSection) {
  return CONTENT.filter((p) => p.section === section).sort((a, b) =>
    b.published.localeCompare(a.published),
  );
}

// Unlike the voice URLs these have a real lastmod: the page's `updated` date.
export const contentSitemapUrls: (SitemapUrl & { lastmod?: Date })[] = [
  { loc: "/blog", priority: 0.7, changefreq: "weekly" },
  { loc: "/compare", priority: 0.7, changefreq: "weekly" },
  ...CONTENT.map((p) => ({
    loc: contentPath(p),
    priority: 0.8,
    changefreq: "monthly" as const,
    lastmod: new Date(`${p.updated}T00:00:00Z`),
  })),
];
